import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';
import { DocumentStatus } from '~/types/document';
import { FlowExtendedPaymentStatus, FlowPaymentStatus, FlowStatusFilterValue } from '~/types/flow';
import SimpleSelect from '../inputs/Select/SimpleSelect';

type NameType<T> = T extends string ? string : T extends {} ? keyof T : string;

interface FlowPaymentStatusFilterProps<Filters extends string | {}> {
  className?: string;
  label?: string;
  placeholder?: string;
  name: NameType<Filters>;
  onChange: (name: NameType<Filters>, value: FlowStatusFilterValue | null) => void;
  value: FlowStatusFilterValue | null;
  isInFlow?: boolean;
  clearable?: boolean;
  hideLabel?: boolean;
  /** When true, adds the "scheduled payment" option (only relevant for the outstanding table). */
  showScheduled?: boolean;
}

const FlowPaymentStatusFilter = <Filters extends string | {}>({
  className,
  label,
  placeholder,
  name,
  onChange,
  isInFlow,
  value,
  clearable,
  hideLabel,
  showScheduled,
}: FlowPaymentStatusFilterProps<Filters>) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'flow_payment_status_filters' });

  return (
    <SimpleSelect
      className={className}
      label={label ?? t('label')}
      placeholder={placeholder ?? label ?? t('label')}
      name={name}
      value={value}
      clearable={clearable}
      hideLabel={hideLabel}
      onChange={(value) => onChange(name, value as FlowStatusFilterValue | null)}
      options={[
        {
          label: t(isNil(isInFlow) ? 'generic.to_pay' : isInFlow ? 'inflow.to_pay' : 'outflow.to_pay'),
          value: FlowPaymentStatus.ToPay,
        },
        ...(showScheduled
          ? [
              {
                label: t('generic.scheduled'),
                value: FlowExtendedPaymentStatus.PaymentScheduled,
              },
            ]
          : []),
        {
          label: t(isNil(isInFlow) ? 'generic.paid' : isInFlow ? 'inflow.paid' : 'outflow.paid'),
          value: FlowPaymentStatus.Paid,
        },
        {
          label: t('generic.draft'),
          value: DocumentStatus.Draft,
        },
      ]}
    />
  );
};

export default FlowPaymentStatusFilter;
